/**
 * Permission Control System
 * Manages role-based access control across the platform
 */

export const PERMISSIONS = {
  // Songs Module
  SONGS_VIEW: 'songs:view',
  SONGS_CREATE: 'songs:create',
  SONGS_EDIT: 'songs:edit',
  SONGS_DELETE: 'songs:delete',
  SONGS_EXPORT: 'songs:export',

  // Artists Module
  ARTISTS_VIEW: 'artists:view',
  ARTISTS_CREATE: 'artists:create',
  ARTISTS_EDIT: 'artists:edit',
  ARTISTS_DELETE: 'artists:delete',
  ARTISTS_EXPORT: 'artists:export',

  // Albums Module
  ALBUMS_VIEW: 'albums:view',
  ALBUMS_CREATE: 'albums:create',
  ALBUMS_EDIT: 'albums:edit',
  ALBUMS_DELETE: 'albums:delete',
  ALBUMS_EXPORT: 'albums:export',

  // Ringtones Module
  RINGTONES_VIEW: 'ringtones:view',
  RINGTONES_CREATE: 'ringtones:create',
  RINGTONES_EDIT: 'ringtones:edit',
  RINGTONES_DELETE: 'ringtones:delete',
  RINGTONES_EXPORT: 'ringtones:export',

  // Record Labels Module
  RECORD_LABELS_VIEW: 'record_labels:view',
  RECORD_LABELS_CREATE: 'record_labels:create',
  RECORD_LABELS_EDIT: 'record_labels:edit',
  RECORD_LABELS_DELETE: 'record_labels:delete',
  RECORD_LABELS_EXPORT: 'record_labels:export',

  // Distributor Module
  DISTRIBUTOR_VIEW: 'distributor:view',
  DISTRIBUTOR_CREATE: 'distributor:create',
  DISTRIBUTOR_EDIT: 'distributor:edit',
  DISTRIBUTOR_DELETE: 'distributor:delete',
  DISTRIBUTOR_EXPORT: 'distributor:export',

  // Revenue Module
  REVENUE_VIEW: 'revenue:view',
  REVENUE_EXPORT: 'revenue:export',
  REVENUE_MARK_PAID: 'revenue:mark_paid',

  // Calendar Module
  CALENDAR_VIEW: 'calendar:view',
  CALENDAR_CREATE: 'calendar:create',
  CALENDAR_EDIT: 'calendar:edit',
  CALENDAR_DELETE: 'calendar:delete',

  // E-Accounts Module
  EACCOUNTS_VIEW: 'eaccounts:view',
  EACCOUNTS_CREATE: 'eaccounts:create',
  EACCOUNTS_EDIT: 'eaccounts:edit',
  EACCOUNTS_DELETE: 'eaccounts:delete',

  // Ownership Module
  OWNERSHIP_VIEW: 'ownership:view',
  OWNERSHIP_CREATE: 'ownership:create',
  OWNERSHIP_EDIT: 'ownership:edit',
  OWNERSHIP_DELETE: 'ownership:delete',

  // Settings Module
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',

  // Notes/Cases Module
  NOTES_CASES_VIEW: 'notes_cases:view',
  NOTES_CASES_CREATE: 'notes_cases:create',
  NOTES_CASES_EDIT: 'notes_cases:edit',
  NOTES_CASES_DELETE: 'notes_cases:delete',
};

export const ROLES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  USER: 'User',
  VIEWER: 'Viewer',
};

/**
 * Normalize a module/action permission check.
 * Handles both 'songs:view' permission_name format AND
 * DB row format where tab_name='Songs' action='read'.
 * Treats 'view' and 'read' as synonyms.
 */
const matchPermission = (p, reqModule, reqAction) => {
  // Normalize
  const normalizeAction = (a) => {
    if (!a) return '';
    const lower = a.toLowerCase();
    if (lower === 'view') return 'read';
    if (lower === 'update') return 'edit';
    return lower;
  };

  const normalizeModule = (m) => {
    if (!m) return '';

    const aliases = {
      'songs': 'songs',
      'song': 'songs',
      'artists': 'artists',
      'artist': 'artists',
      'albums': 'albums',
      'album': 'albums',
      'ringtones': 'ringtones',
      'ringtone': 'ringtones',
      'ringintone': 'ringtones',
      'record_labels': 'record_labels',
      'record_label': 'record_labels',
      'recordlabels': 'record_labels',
      'recordlabel': 'record_labels',
      'recode_labels': 'record_labels',
      'recode_label': 'record_labels',
      'recodelabels': 'record_labels',
      'recodelabel': 'record_labels',
      'distributor': 'distributor',
      'distributors': 'distributor',
      'revenue': 'revenue',
      'calendar': 'calendar',
      'eaccounts': 'eaccounts',
      'e_account': 'eaccounts',
      'eaccount': 'eaccounts',
      'ownership': 'ownership',
      'notes_cases': 'notes_cases',
      'notes_and_cases': 'notes_cases',
      'notesandcases': 'notes_cases',
      'settings': 'settings'
    };

    // Convert e.g. 'Notes & Cases' -> 'notes_cases', 'E-Accounts' -> 'eaccounts'
    const normalized = m.toLowerCase()
      .replace(/\s*&\s*/g, '_')
      .replace(/-/g, '')
      .replace(/\s+/g, '_');

    return aliases[normalized] || normalized;
  };

  const nReqAction = normalizeAction(reqAction);
  const nReqModule = normalizeModule(reqModule);

  // 1) Match by permission_name string e.g. 'songs:view', 'songs:read'
  const permName = (p.permission_name || '').toLowerCase();
  if (permName) {
    const [pMod, pAct] = permName.split(':');
    if (normalizeModule(pMod) === nReqModule && normalizeAction(pAct) === nReqAction) {
      return true;
    }
  }

  // 2) Match by tab_name + action DB columns
  const pTabNorm = normalizeModule(p.tab_name || '');
  const pActionNorm = normalizeAction(p.action || '');
  if (pTabNorm && pTabNorm === nReqModule && pActionNorm === nReqAction) {
    return true;
  }

  return false;
};

/**
 * Check if user has specific permission
 * @param {Object} user - User object with roles/permissions
 * @param {string} permission - Permission to check e.g. 'songs:view'
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;

  // Access is granted only when the user has an explicit permission entry.
  // A role name alone should not bypass the permission matrix.
  if (!user.permissions || !Array.isArray(user.permissions)) return false;

  const [reqModule, reqAction] = permission.toLowerCase().split(':');

  return user.permissions.some(p => matchPermission(p, reqModule, reqAction));
};

/**
 * Check if user has multiple permissions (any match)
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user || !Array.isArray(permissions)) return false;
  return permissions.some(perm => hasPermission(user, perm));
};

/**
 * Check if user has all permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user || !Array.isArray(permissions)) return false;
  return permissions.every(perm => hasPermission(user, perm));
};

/**
 * Check if user can view a module
 * @param {Object} user - User object
 * @param {string} moduleName - Module name (e.g., 'songs', 'artists')
 * @returns {boolean}
 */
export const canViewModule = (user, moduleName) => {
  if (!user) return false;

  // Check both 'view' and 'read' since backend stores 'read' for view permission
  return hasPermission(user, `${moduleName.toLowerCase()}:view`);
};

/**
 * Check if user can create in a module
 * @param {Object} user - User object
 * @param {string} moduleName - Module name
 * @returns {boolean}
 */
export const canCreate = (user, moduleName) => {
  if (!user) return false;

  return hasPermission(user, `${moduleName.toLowerCase()}:create`);
};

/**
 * Check if user can edit in a module
 * @param {Object} user - User object
 * @param {string} moduleName - Module name
 * @returns {boolean}
 */
export const canEdit = (user, moduleName) => {
  if (!user) return false;

  // Check both 'edit' and 'update' since backend may store 'update'
  return hasPermission(user, `${moduleName.toLowerCase()}:edit`) ||
         hasPermission(user, `${moduleName.toLowerCase()}:update`);
};

/**
 * Check if user can delete in a module
 * @param {Object} user - User object
 * @param {string} moduleName - Module name
 * @returns {boolean}
 */
export const canDelete = (user, moduleName) => {
  if (!user) return false;

  return hasPermission(user, `${moduleName.toLowerCase()}:delete`);
};

/**
 * Check if user can export from a module
 * @param {Object} user - User object
 * @param {string} moduleName - Module name
 * @returns {boolean}
 */
export const canExport = (user, moduleName) => {
  if (!user) return false;

  return hasPermission(user, `${moduleName.toLowerCase()}:export`);
};

/**
 * Get accessible modules for user
 * @param {Object} user - User object
 * @returns {string[]} Array of accessible module names
 */
export const getAccessibleModules = (user) => {
  if (!user) return [];

  if (!user.permissions || !Array.isArray(user.permissions)) return [];

  const modules = new Set();
  user.permissions.forEach(p => {
    // Extract module from permission_name string ('songs:view') OR from tab_name column
    let moduleName = '';
    if (p.permission_name) {
      moduleName = (p.permission_name || '').split(':')[0];
    } else if (p.tab_name) {
      moduleName = p.tab_name.toLowerCase()
        .replace(/\s*&\s*/g, '_')
        .replace(/-/g, '')
        .replace(/\s+/g, '_');
    }
    if (moduleName) modules.add(moduleName);
  });

  return Array.from(modules);
};

/**
 * Get module actions available to user
 * @param {Object} user - User object
 * @param {string} moduleName - Module name
 * @returns {Object} Object with view, create, edit, delete, export flags
 */
export const getModuleActions = (user, moduleName) => {
  const module = moduleName.toLowerCase();
  return {
    view: canViewModule(user, module),
    create: canCreate(user, module),
    edit: canEdit(user, module),
    delete: canDelete(user, module),
    export: canExport(user, module),
  };
};
